import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';

import { MenuItem } from 'primereact/menuitem';
import { BreadCrumb } from 'primereact/breadcrumb';

import LoadingSpinner from 'components/Misc/LoadingSpinner';
import { getOneProject } from 'services/title-report/project';
import ErrorFallback from 'components/ErrorFallback';

const Projects = () => {
    const location = useLocation();

    const [projectOid, setProjectOid] = useState('');
    const [breadCrumbItems, setBreadCrumbItems] = useState<MenuItem[]>([]);

    const { data: currentProject, error } = useQuery<
        Ether.TitlesReport.IProject | undefined,
        Error
    >(
        ['get-one-project-maybe', projectOid],
        async (): Promise<Ether.TitlesReport.IProject | undefined> => {
            const project = await getOneProject(projectOid);
            if (project) return project;
        },
        {
            enabled: projectOid !== '',
        }
    );

    const isAvailable = (path: string) => {
        return path != null && path !== '';
    };

    const getTemplate = (item: MenuItem) => {
        const MenuText = <span className='p-menuitem-text'>{item.label}</span>;
        if (item.url) {
            return (
                <Link to={item.url} className='p-menuitem-link'>
                    {MenuText}
                </Link>
            );
        }
        return MenuText;
    };

    useEffect(() => {
        let items: MenuItem[] = [
            {
                label: 'Projects',
                url: '/projects',
            },
        ];
        const paths = location.pathname.split('/');
        if (!isAvailable(paths[2])) items[0] = { label: 'Projects' };
        if (isAvailable(paths[2])) {
            setProjectOid(paths[2]);
            if (currentProject) items.push({ label: currentProject.name });
            else {
                setBreadCrumbItems([]);
                return;
            }
        }
        if (isAvailable(paths[3])) {
            if (paths[3] === 'reports') items.push({ label: 'Reports' });
            else if (paths[3] === 'check-links')
                items.push({ label: 'Links check' });
            else if (paths[3] === 'inbox') items.push({ label: 'Link inbox' });
            else
                items.push({
                    label: 'VideoID Validation',
                    url: isAvailable(paths[4])
                        ? `${paths[2]}/videoid-validation`
                        : undefined,
                });
        }
        if (isAvailable(paths[4])) {
            if (paths[4] === 'matches') items.push({ label: 'Matches' });
        }
        items = items.map((item) => ({ ...item, template: getTemplate }));
        setBreadCrumbItems(items);
    }, [location, currentProject]);

    return (
        <div>
            {error ? (
                error.message
            ) : projectOid === '' || (projectOid !== '' && currentProject) ? (
                <BreadCrumb model={breadCrumbItems} />
            ) : (
                <LoadingSpinner />
            )}
            <ErrorBoundary
                key={location.pathname}
                FallbackComponent={ErrorFallback}
            >
                <Outlet />
            </ErrorBoundary>
        </div>
    );
};

export default Projects;
